'use client';

import React, { forwardRef } from 'react';
import type { MarketPulse, AbstractFearGreed } from '@/hooks/useMarketAnalysis';

interface TopMover {
  symbol: string;
  change: number;
  imageUrl?: string | null;
}

interface TvlDataPoint {
  date: string;
  tvl: number;
}

interface TokenShareCardProps {
  pulse: MarketPulse;
  abstractFearGreed?: AbstractFearGreed | null;
  topGainers?: TopMover[];
  topLosers?: TopMover[];
  historicalTvl?: TvlDataPoint[];
  currentTvl?: number;
  tvlChange24h?: number;
}

/**
 * Get Fear/Greed color config
 */
function getFearGreedConfig(value: number) {
  if (value <= 25) return { color: '#14b8a6', label: 'Extreme Fear' };
  if (value <= 45) return { color: '#10b981', label: 'Fear' };
  if (value <= 55) return { color: '#eab308', label: 'Neutral' };
  if (value <= 75) return { color: '#f97316', label: 'Greed' };
  return { color: '#ef4444', label: 'Extreme Greed' };
}

/**
 * Mini TVL sparkline chart for share card
 */
function MiniTvlChart({ data, isPositive }: { data: TvlDataPoint[]; isPositive: boolean }) {
  if (!data || data.length < 2) return null;

  const width = 200;
  const height = 50;
  const padding = 4;

  const tvlValues = data.map(d => d.tvl);
  const minTvl = Math.min(...tvlValues);
  const maxTvl = Math.max(...tvlValues);
  const range = maxTvl - minTvl || 1;

  const chartWidth = width - padding * 2;
  const chartHeight = height - padding * 2;

  const points = data.map((point, index) => {
    const x = padding + (index / (data.length - 1)) * chartWidth;
    const y = padding + chartHeight - ((point.tvl - minTvl) / range) * chartHeight;
    return { x, y };
  });

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)},${p.y.toFixed(1)}`)
    .join(' ');

  const areaPath = `${linePath} L ${points[points.length - 1].x.toFixed(1)},${height - padding} L ${padding},${height - padding} Z`;

  const strokeColor = isPositive ? '#84cc16' : '#ef4444';
  const gradientId = 'token-tvl-gradient';

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={strokeColor} stopOpacity="0.3" />
          <stop offset="100%" stopColor={strokeColor} stopOpacity="0.05" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradientId})`} />
      <path
        d={linePath}
        fill="none"
        stroke={strokeColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Semi-circle gauge for share card (inline SVG for image generation)
 */
function ShareCardGauge({ value }: { value: number }) {
  const config = getFearGreedConfig(value);
  const needleRotation = -90 + (value / 100) * 180;
  
  const size = 100;
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const centerX = size / 2;
  const centerY = size / 2;

  return (
    <div style={{ position: 'relative', width: size, height: size / 2 + 10 }}>
      <svg 
        width={size} 
        height={size / 2 + 10}
        viewBox={`0 0 ${size} ${size / 2 + 10}`}
        style={{ overflow: 'visible' }}
      >
        <defs>
          <linearGradient id="token-share-gauge-grad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#14b8a6" />
            <stop offset="25%" stopColor="#10b981" />
            <stop offset="50%" stopColor="#eab308" />
            <stop offset="75%" stopColor="#f97316" />
            <stop offset="100%" stopColor="#ef4444" />
          </linearGradient>
        </defs>
        
        {/* Background arc */}
        <path
          d={`M ${strokeWidth / 2} ${centerY} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${centerY}`}
          fill="none"
          stroke="#1a1a1a"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        
        {/* Colored gradient arc */}
        <path
          d={`M ${strokeWidth / 2} ${centerY} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${centerY}`}
          fill="none"
          stroke="url(#token-share-gauge-grad)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        
        {/* Needle */}
        <g transform={`rotate(${needleRotation} ${centerX} ${centerY})`}>
          <line
            x1={centerX}
            y1={centerY - 6}
            x2={centerX}
            y2={centerY - radius * 0.7}
            stroke={config.color}
            strokeWidth={3}
            strokeLinecap="round"
          />
          <polygon
            points={`
              ${centerX},${centerY - radius * 0.7 - 5}
              ${centerX - 4},${centerY - radius * 0.7 + 4}
              ${centerX + 4},${centerY - radius * 0.7 + 4}
            `}
            fill={config.color}
          />
        </g>
        
        {/* Center dot */}
        <circle cx={centerX} cy={centerY} r={6} fill={config.color} />
        <circle cx={centerX} cy={centerY} r={3} fill="#0a0a0a" />
      </svg>
    </div>
  );
}

/**
 * Token Market Share Card
 * Clean token-focused layout with F&G gauge, key stats, and top movers
 */
const TokenShareCard = forwardRef<HTMLDivElement, TokenShareCardProps>(
  ({ pulse, abstractFearGreed, topGainers = [], topLosers = [], historicalTvl, currentTvl, tvlChange24h }, ref) => {
    
    const formatNumber = (num: number): string => {
      if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
      if (num >= 1e6) return `$${(num / 1e6).toFixed(1)}M`;
      if (num >= 1e3) return `$${(num / 1e3).toFixed(1)}K`;
      return `$${num.toFixed(0)}`;
    };

    const fgValue = abstractFearGreed?.value ?? 50;
    const fgConfig = getFearGreedConfig(fgValue);

    const timestamp = new Date().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    const tvl = pulse.ecosystemContext?.tvl || 0;
    const volume = pulse.ecosystemContext?.dexVolume24h || 0;
    const protocols = pulse.ecosystemContext?.protocolCount || 0;

    // Use the market pulse summary - token-focused insight
    const marketInsight = pulse.summary;

    // Display top 3 gainers and losers
    const displayGainers = topGainers.slice(0, 3);
    const displayLosers = topLosers.slice(0, 3);

    // Gradient glow config based on market sentiment
    const getGlowConfig = () => {
      const gainers = pulse.metrics.gainersCount || 0;
      const losers = pulse.metrics.losersCount || 0;
      
      if (gainers > losers * 1.5) {
        // Bullish - green glow
        return {
          bgGlow: 'radial-gradient(ellipse at top right, rgba(16, 185, 129, 0.12) 0%, transparent 50%)',
          accentGradient: 'linear-gradient(135deg, #059669 0%, #10b981 50%, #34d399 100%)',
          accentColor: '#10b981',
        };
      } else if (losers > gainers * 1.5) {
        // Bearish - red glow
        return {
          bgGlow: 'radial-gradient(ellipse at top right, rgba(239, 68, 68, 0.12) 0%, transparent 50%)',
          accentGradient: 'linear-gradient(135deg, #dc2626 0%, #ef4444 50%, #f87171 100%)',
          accentColor: '#ef4444',
        };
      } else {
        // Neutral/Mixed - teal glow (Abstract brand)
        return {
          bgGlow: 'radial-gradient(ellipse at top right, rgba(20, 184, 166, 0.12) 0%, transparent 50%)',
          accentGradient: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 50%, #2dd4bf 100%)',
          accentColor: '#14b8a6',
        };
      }
    };

    const glowConfig = getGlowConfig();

    return (
      <div
        ref={ref}
        style={{
          width: '480px',
          maxWidth: '100%',
          minWidth: '360px',
          padding: '24px',
          background: 'linear-gradient(180deg, #0a0a0a 0%, #171717 100%)',
          borderRadius: '16px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          position: 'relative',
          boxSizing: 'border-box',
          border: '1px solid #262626',
          overflow: 'hidden',
        }}
      >
        {/* Background glow effect */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            background: glowConfig.bgGlow,
            pointerEvents: 'none',
          }}
        />

        {/* Decorative corner accent */}
        <div
          style={{
            position: 'absolute',
            top: '-50px',
            right: '-50px',
            width: '200px',
            height: '200px',
            background: glowConfig.accentGradient,
            opacity: 0.1,
            borderRadius: '50%',
            filter: 'blur(60px)',
          }}
        />
        {/* === MAIN HEADER: ABSTRACT FEAR & GREED INDEX === */}
        <div 
          style={{ 
            marginBottom: '20px',
            position: 'relative',
          }}
        >
          {/* Title with Abstract logo */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            marginBottom: '16px',
          }}>
            <img
              src="/Abstract_AppIcon_DarkMode.webp"
              alt="Abstract"
              style={{ width: '36px', height: '36px', borderRadius: '8px' }}
            />
            <div>
              <div style={{ fontSize: '18px', fontWeight: 700, color: '#fff', letterSpacing: '-0.02em' }}>
                Abstract Fear & Greed Index
              </div>
              <div style={{ fontSize: '11px', color: '#525252', marginTop: '2px' }}>
                {timestamp}
              </div>
            </div>
          </div>
          
          {/* Gauge Section - Made more prominent */}
          <div 
            style={{ 
              padding: '20px',
              background: '#111',
              borderRadius: '12px',
            }}
          >
            <div 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                gap: '28px',
              }}
            >
              <ShareCardGauge value={fgValue} />
              
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: 'monospace', fontSize: '42px', fontWeight: 700, color: fgConfig.color, lineHeight: 1 }}>
                  {fgValue}
                </div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: fgConfig.color, marginTop: '6px' }}>
                  {fgConfig.label}
                </div>
              </div>
            </div>
            
            {/* What it measures */}
            <div style={{ 
              marginTop: '14px', 
              textAlign: 'center',
              fontSize: '10px',
              color: '#525252',
            }}>
              Measuring Abstract ecosystem sentiment via price momentum, volume, TVL & protocol activity
            </div>
          </div>
        </div>

        {/* === KEY STATS ROW === */}
        <div 
          style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            background: '#111',
            borderRadius: '10px',
            marginBottom: '16px',
            position: 'relative',
          }}
        >
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '10px', color: '#525252', textTransform: 'uppercase', marginBottom: '4px' }}>TVL</div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#fff' }}>{formatNumber(tvl)}</div>
          </div>
          <div style={{ width: '1px', height: '32px', background: '#262626', flexShrink: 0 }} />
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '10px', color: '#525252', textTransform: 'uppercase', marginBottom: '4px' }}>24H Volume</div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#fff' }}>{formatNumber(volume)}</div>
          </div>
          <div style={{ width: '1px', height: '32px', background: '#262626', flexShrink: 0 }} />
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '10px', color: '#525252', textTransform: 'uppercase', marginBottom: '4px' }}>Protocols</div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#fff' }}>{protocols}</div>
          </div>
        </div>

        {/* === ABSTRACT TVL CHART === */}
        {historicalTvl && historicalTvl.length > 2 && (
          <div
            style={{
              padding: '12px 16px',
              background: '#111',
              borderRadius: '10px',
              marginBottom: '16px',
              position: 'relative',
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px'
            }}>
              <div style={{ fontSize: '10px', color: '#525252', textTransform: 'uppercase', fontWeight: 600 }}>
                Abstract TVL (30D)
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {currentTvl && (
                  <span style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>
                    {formatNumber(currentTvl)}
                  </span>
                )}
                {tvlChange24h !== undefined && (
                  <span style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: tvlChange24h >= 0 ? '#84cc16' : '#ef4444'
                  }}>
                    {tvlChange24h >= 0 ? '+' : ''}{tvlChange24h.toFixed(1)}%
                  </span>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <MiniTvlChart
                data={historicalTvl.slice(-30)}
                isPositive={historicalTvl[historicalTvl.length - 1]?.tvl >= historicalTvl[0]?.tvl}
              />
            </div>
            <div style={{ fontSize: '9px', color: '#404040', textAlign: 'right', marginTop: '4px' }}>
              DeFiLlama
            </div>
          </div>
        )}

        {/* === TOKEN MOVERS SECTION === */}
        <div style={{ marginBottom: '16px', position: 'relative' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '10px' 
          }}>
            <div style={{ fontSize: '11px', color: '#737373', textTransform: 'uppercase', fontWeight: 600 }}>
              TOKEN MOVERS
            </div>
            <div style={{ fontSize: '11px', color: '#525252' }}>
              <span style={{ color: '#10b981', fontWeight: 600 }}>{pulse.metrics.gainersCount} up</span>
              {' / '}
              <span style={{ color: '#ef4444', fontWeight: 600 }}>{pulse.metrics.losersCount} down</span>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '12px' }}>
            {/* Gainers Column */}
            <div style={{ flex: 1, background: 'rgba(16, 185, 129, 0.08)', borderRadius: '10px', padding: '12px' }}>
              <div style={{ fontSize: '10px', color: '#10b981', textTransform: 'uppercase', fontWeight: 600, marginBottom: '8px' }}>
                ▲ Gainers
              </div>
              {displayGainers.length === 0 ? (
                <div style={{ 
                  fontSize: '12px', 
                  color: '#525252', 
                  textAlign: 'center',
                  padding: '16px 8px',
                  fontStyle: 'italic',
                }}>
                  No gainers today
                </div>
              ) : (
                displayGainers.map((token, idx) => (
                  <div 
                    key={token.symbol} 
                    style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '6px 0',
                      borderBottom: idx < displayGainers.length - 1 ? '1px solid rgba(16, 185, 129, 0.15)' : 'none'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {token.imageUrl ? (
                        <img 
                          src={token.imageUrl} 
                          alt={token.symbol}
                          style={{ width: '20px', height: '20px', borderRadius: '50%' }}
                        />
                      ) : (
                        <div style={{ 
                          width: '20px', 
                          height: '20px', 
                          borderRadius: '50%', 
                          background: '#10b981',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px',
                          fontWeight: 700,
                          color: '#fff',
                        }}>
                          {token.symbol.charAt(0)}
                        </div>
                      )}
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#fff' }}>${token.symbol}</span>
                    </div>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#10b981' }}>+{token.change.toFixed(1)}%</span>
                  </div>
                ))
              )}
            </div>
            
            {/* Losers Column */}
            <div style={{ flex: 1, background: 'rgba(239, 68, 68, 0.08)', borderRadius: '10px', padding: '12px' }}>
              <div style={{ fontSize: '10px', color: '#ef4444', textTransform: 'uppercase', fontWeight: 600, marginBottom: '8px' }}>
                ▼ Losers
              </div>
              {displayLosers.length === 0 ? (
                <div style={{ 
                  fontSize: '12px', 
                  color: '#525252', 
                  textAlign: 'center',
                  padding: '16px 8px',
                  fontStyle: 'italic',
                }}>
                  No losers today
                </div>
              ) : (
                displayLosers.map((token, idx) => (
                  <div 
                    key={token.symbol} 
                    style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '6px 0',
                      borderBottom: idx < displayLosers.length - 1 ? '1px solid rgba(239, 68, 68, 0.15)' : 'none'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {token.imageUrl ? (
                        <img 
                          src={token.imageUrl} 
                          alt={token.symbol}
                          style={{ width: '20px', height: '20px', borderRadius: '50%' }}
                        />
                      ) : (
                        <div style={{ 
                          width: '20px', 
                          height: '20px', 
                          borderRadius: '50%', 
                          background: '#ef4444',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px',
                          fontWeight: 700,
                          color: '#fff',
                        }}>
                          {token.symbol.charAt(0)}
                        </div>
                      )}
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#fff' }}>${token.symbol}</span>
                    </div>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#ef4444' }}>{token.change.toFixed(1)}%</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* === AI INSIGHT === */}
        {marketInsight && (
          <div style={{ marginBottom: '16px', position: 'relative' }}>
            <div style={{ fontSize: '10px', color: '#737373', textTransform: 'uppercase', marginBottom: '8px', fontWeight: 600 }}>
              AI MARKET INSIGHT
            </div>
            <div 
              style={{ 
                fontSize: '12px', 
                color: '#a3a3a3', 
                lineHeight: 1.6,
                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
                padding: '12px',
                background: '#111',
                borderRadius: '8px',
              }}
            >
              {marketInsight}
            </div>
          </div>
        )}

        {/* === FOOTER === */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingTop: '14px',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            position: 'relative',
          }}
        >
          <div style={{ fontSize: '9px', color: '#525252', letterSpacing: '0.04em' }}>
            Data: DeFi Llama • DexScreener
          </div>
          {/* White wordmark only (descout-logo-light is black → invisible on dark) */}
          <img
            src="/descout-wordmark-white.svg"
            alt="Descout"
            style={{ height: '16px', width: 'auto' }}
          />
        </div>
      </div>
    );
  }
);

TokenShareCard.displayName = 'TokenShareCard';

export default TokenShareCard;
